import type { Change as PmChange } from "prosemirror-changeset"
import type { Node } from "prosemirror-model"
import { ChangeSet } from "prosemirror-changeset"

import { recreateTransformBlockwise } from "./recreate-blockwise"

export type Change =
	| {
			type: "insert"
			rangeA: { pos: number }
			rangeB: { from: number; to: number }
	  }
	| {
			type: "delete"
			rangeA: { from: number; to: number }
			rangeB: { pos: number }
	  }
	| {
			type: "replace"
			rangeA: { from: number; to: number }
			rangeB: { from: number; to: number }
	  }

export interface DiffOptions {
	simplifyDiff?: boolean
	wordDiffs?: boolean
}

export function diffDocs(
	docA: Node,
	docB: Node,
	options: DiffOptions = {},
): Change[] {
	const transform = recreateTransformBlockwise(docA, docB, {
		complexSteps: true,
		wordDiffs: options.wordDiffs ?? true,
		simplifyDiff: options.simplifyDiff ?? true,
	})

	if (transform.steps.length === 0) {
		return []
	}

	const changeSet = ChangeSet.create(docA).addSteps(
		transform.doc,
		transform.mapping.maps,
		null,
	)

	return changeSet.changes.map(toChange)
}

function toChange(c: PmChange): Change {
	const aLen = c.toA - c.fromA
	const bLen = c.toB - c.fromB

	if (aLen === 0 && bLen > 0) {
		return {
			type: "insert",
			rangeA: { pos: c.fromA },
			rangeB: { from: c.fromB, to: c.toB },
		}
	}
	if (bLen === 0 && aLen > 0) {
		return {
			type: "delete",
			rangeA: { from: c.fromA, to: c.toA },
			rangeB: { pos: c.fromB },
		}
	}
	return {
		type: "replace",
		rangeA: { from: c.fromA, to: c.toA },
		rangeB: { from: c.fromB, to: c.toB },
	}
}
